/**
 * Marches, as things that are happening rather than things that have happened.
 *
 * The strategic layer already charged a march in turns — `moveT` counted down
 * and the army appeared at the far end — but nothing on the board could see the
 * army while it was on the road. It was in its origin province for defensive
 * purposes, in neither province for battle purposes, and nowhere at all as far
 * as the map was concerned. Marching took time without the time being *worth*
 * anything: you could not watch a host coming, and you could not do anything
 * about one that was.
 *
 * So a march is now a lane: an origin, a destination, a total, and a remainder.
 * Three things follow from stating it that way, and they are the whole of this
 * file.
 *
 *   **It can be drawn.** `marchesInProgress` hands the map a list of lanes with
 *   a 0..1 progress on each, so a counter sits between two provinces.
 *
 *   **It can be turned around.** Halting swaps the two ends of the lane, so an
 *   army that changes its mind pays for the ground it has already covered. It
 *   does not teleport home.
 *
 *   **It can be caught.** Two hosts crossing on the same road now meet on it,
 *   and a host standing at either end may be ordered to run one down. Both fight
 *   through the existing battle path as `BattleKind.Road`: nothing changes hands,
 *   the loser is thrown back where it came from.
 *
 * The cost of a march is still the link's `march` value, which already prices
 * distance and terrain. Nothing here invents a second notion of distance.
 */

import { LINK_NAMES, LinkKind } from '../../data/provinces.ts';
import { linkBetween } from './graph.ts';
import { armyPoints, findArmy } from './armies.ts';
import { atWar, logLine, provinceName } from './state.ts';
import {
  ROAD_BATTLE_DELAY,
  ROAD_LOSER_REGROUP,
  type Army,
  type CampaignState,
} from './types.ts';

/** A march on the board, in the form the world map wants to draw it. */
export interface MarchLane {
  armyId: number;
  owner: number;
  from: number;
  to: number;
  /** 0 at the moment of setting out, 1 on the turn it arrives. */
  progress: number;
  turnsLeft: number;
  /** Strength points, so the map can size the counter without a second import. */
  points: number;
}

export function isMarching(a: Army): boolean {
  return a.moveTo >= 0 && a.moveFrom >= 0;
}

/**
 * How far along its road an army is, 0..1.
 *
 * Reported as a fraction of *completed* turns rather than of remaining ones so
 * that an army which has just set out reads as 0 and one arriving this turn
 * reads close to 1, which is the direction a player's eye expects to travel.
 */
export function marchProgress(a: Army): number {
  if (!isMarching(a) || a.moveTotal <= 0) return 0;
  const done = a.moveTotal - a.moveT;
  return Math.max(0, Math.min(1, done / a.moveTotal));
}

/** Every march currently on the roads, in army-id order. */
export function marchesInProgress(c: CampaignState): MarchLane[] {
  const out: MarchLane[] = [];
  for (const a of c.armies) {
    if (!isMarching(a)) continue;
    out.push({
      armyId: a.id,
      owner: a.owner,
      from: a.moveFrom,
      to: a.moveTo,
      progress: marchProgress(a),
      turnsLeft: a.moveT,
      points: Math.round(armyPoints(a)),
    });
  }
  out.sort((x, y) => x.armyId - y.armyId);
  return out;
}

/** Marches currently crossing the link between `p` and `q`, either way. */
export function marchesOnLink(c: CampaignState, p: number, q: number): Army[] {
  return c.armies.filter(
    (a) =>
      isMarching(a) &&
      ((a.moveFrom === p && a.moveTo === q) || (a.moveFrom === q && a.moveTo === p)),
  );
}

/**
 * Turn a march around.
 *
 * The lane is reversed rather than cancelled: an army three turns into a
 * five-turn crossing of the Deepgate pass has three turns of walking to do
 * before it is home again. Cancelling outright would make a march free to
 * un-order, which would make ordering one costless, which would make the whole
 * of this file decoration.
 */
export function haltMarch(a: Army): boolean {
  if (!isMarching(a)) return false;
  // Halting cancels the *order*, not just the leg. Without this a host told to
  // stop would turn round, land where it started, and then dutifully set off
  // again toward the destination it was still carrying.
  a.dest = -1;
  const covered = a.moveTotal - a.moveT;
  const home = a.moveFrom;
  a.moveFrom = a.moveTo;
  a.moveTo = home;
  a.moveT = covered;
  a.intercepting = -1;
  if (a.moveT <= 0) {
    // It had not left yet. Standing still is a legal outcome of turning around.
    landArmy(a, home);
  }
  return true;
}

/** Put an army down in a province and clear everything about its march. */
export function landArmy(a: Army, province: number): void {
  a.at = province;
  a.moveTo = -1;
  a.moveT = 0;
  a.moveFrom = -1;
  a.moveTotal = 0;
  a.intercepting = -1;
}

/**
 * Put an army down somewhere it was *not* going, and drop the order with it.
 *
 * The difference from `landArmy` is one line and the whole of a bug class.
 * `landArmy` is what a leg landing calls, and it must leave `dest` alone —
 * `continueMarch` reads it half a line later to take the next leg. Every *other*
 * caller is putting a host somewhere it did not choose: thrown off a road,
 * retreated off a province, walked off a ship. Those must drop `dest`, because
 * nothing in the turn machine will ever pick it up again — `continueMarch` is
 * only ever reached from a leg that landed, so a host set down any other way
 * keeps a destination it can never walk to.
 *
 * Measured before this existed: 77 army-months a campaign, worst 155, spent
 * standing still holding an order that had already been abandoned. Half of that
 * is a host the player told to go somewhere and which quietly stopped; the map
 * drew the ring on the destination either way.
 */
export function standDown(a: Army, province: number): void {
  landArmy(a, province);
  a.dest = -1;
}

// ── Interception ───────────────────────────────────────────────────────────

/**
 * Whether `a` may set off after `target`.
 *
 * The rule is one sentence: you can run down a host on a road that touches the
 * province you are standing in. That keeps interception a *positional*
 * advantage — being on the right border is what buys it, not a stat — and it
 * keeps it readable, because the player can see the road and see themselves
 * standing on one end of it.
 */
export function canIntercept(c: CampaignState, a: Army, target: Army): boolean {
  if (a.id === target.id) return false;
  if (isMarching(a)) return false;
  // A host still picking itself up after a fight cannot go out after anybody,
  // and cannot be piled on to while it does. Both halves matter: without the
  // second, the loser of a road battle is the easiest target on the board on
  // the very month it can least answer for itself.
  if (a.regroup > 0 || target.regroup > 0) return false;
  // A host that has gone inside the walls has given up the road. Running
  // somebody down is exactly the thing shelter is a refusal of.
  if (a.sheltered) return false;
  if (a.regiments.length === 0 || target.regiments.length === 0) return false;
  if (!isMarching(target)) return false;
  if (a.owner === target.owner) return false;
  if (!atWar(c, a.owner, target.owner)) return false;
  return target.moveFrom === a.at || target.moveTo === a.at;
}

export function orderIntercept(c: CampaignState, a: Army, target: Army): boolean {
  if (!canIntercept(c, a, target)) return false;
  a.intercepting = target.id;
  return true;
}

/**
 * A pair of hosts about to meet on a road, and the road they meet on.
 *
 * `attacker` is whoever forced the contact: the interceptor when one was
 * ordered, and otherwise the lower army id, which is arbitrary and — far more
 * importantly — the same every time the same board is played.
 */
export interface RoadContact {
  attacker: Army;
  defender: Army;
  /** The lane, from the attacker's point of view. */
  from: number;
  to: number;
  /** Army id that went out to force this, or -1 when the two simply met. */
  interceptor: number;
}

/**
 * Find the next road contact, or null.
 *
 * Called repeatedly by the turn machine, because each contact is fought and
 * applied before the next is looked for — the same one-at-a-time discipline
 * province battles already use, and for the same reason: a battle can suspend
 * the turn while the player fights it by hand.
 *
 * Ordered interceptions come first. Two hosts blundering into each other on the
 * same road is a fact about the board; an interception is a decision somebody
 * made, and a decision should not be pre-empted by an accident.
 */
export function nextRoadContact(c: CampaignState): RoadContact | null {
  const chasers = c.armies
    .filter((a) => a.intercepting >= 0 && !isMarching(a) && a.regiments.length > 0 && a.regroup <= 0)
    .sort((x, y) => x.id - y.id);
  for (const chaser of chasers) {
    const target = findArmy(c, chaser.intercepting);
    if (!target || !canIntercept(c, chaser, target)) {
      // The quarry arrived, died, or turned around between the order and the
      // march. A chase that has nothing to chase is an ordinary event.
      chaser.intercepting = -1;
      continue;
    }
    return {
      attacker: chaser,
      defender: target,
      from: target.moveFrom,
      to: target.moveTo,
      interceptor: chaser.id,
    };
  }

  // Head-on: two hosts at war crossing the same link in opposite directions.
  // Without this they swap provinces and never see each other, which is the
  // oldest bug in strategic games and reads as the map cheating.
  const marching = c.armies
    .filter((a) => isMarching(a) && a.regiments.length > 0 && a.regroup <= 0)
    .sort((x, y) => x.id - y.id);
  for (let i = 0; i < marching.length; i++) {
    for (let j = i + 1; j < marching.length; j++) {
      const a = marching[i];
      const b = marching[j];
      if (a.owner === b.owner) continue;
      if (!atWar(c, a.owner, b.owner)) continue;
      if (a.moveFrom !== b.moveTo || a.moveTo !== b.moveFrom) continue;
      return { attacker: a, defender: b, from: a.moveFrom, to: a.moveTo, interceptor: -1 };
    }
  }
  return null;
}

/**
 * What a road battle does to the marches that produced it.
 *
 * The loser is thrown back to the end of the road it set out from, arriving
 * there immediately — it has spent the turn running, not walking — and drops the
 * order it was carrying. The winner keeps its lane but spends the month on the
 * battle rather than on the road. An interceptor that wins ends up back where it
 * was standing: it went out to the road and came back. Both sides regroup, the
 * loser for longer.
 *
 * ── The running fight, and why the first version of this looped ────────────
 *
 * *"i keep fightinting army while attacking and they are walking towards me but
 * its really broken"*. It was, and `src/tools/roadfight.ts` says by how much:
 * **42 of every 140 road battles in a campaign were the same two hosts meeting
 * again**, in runs of up to five consecutive months, ending only when one of
 * them died. Neither of the two things that should have separated them did.
 *
 * The loser kept its order. Thrown back to the province it set out from with
 * `dest` untouched and nothing stopping it, the council sent it straight back
 * down the same road the same month — 171 times a campaign, measured. `standDown`
 * and `regroup` are the two halves of the answer: the order goes, and the host
 * is not in a state to be given a new one until the month after next.
 *
 * The winner never actually paid the delay. `TurnPhase.Roads` runs *before*
 * `TurnPhase.March`, so adding a month to `moveT` here and taking one off in
 * `doMarch` netted to zero — the host stood still, which is right, but its
 * `moveTotal` grew every single time, so the counter the map draws between two
 * provinces slid *backwards* down the road it was walking: 3/3, then 3/4, then
 * 3/5. `doMarch` now spends the month on `regroup` and leaves the lane alone,
 * which is the same standstill said once instead of twice, and the drawn
 * progress holds still where the host is holding still.
 */
export function settleRoadBattle(
  c: CampaignState,
  winner: Army | undefined,
  loser: Army | undefined,
  interceptorId: number,
): void {
  if (loser && loser.regiments.length > 0) {
    const home = isMarching(loser) ? loser.moveFrom : loser.at;
    standDown(loser, home);
    loser.supply = Math.min(loser.supply, 0.6);
    loser.regroup = ROAD_LOSER_REGROUP;
  }
  if (winner && winner.regiments.length > 0) {
    if (winner.id === interceptorId) standDown(winner, winner.at);
    winner.regroup = Math.max(winner.regroup, ROAD_BATTLE_DELAY);
  }
  const chaser = interceptorId >= 0 ? findArmy(c, interceptorId) : undefined;
  if (chaser) chaser.intercepting = -1;
}

/**
 * The road a contact is fought on, named the way the chronicle wants it.
 *
 * The link already knows whether it is an old road, a ford or a pass, and saying
 * so is most of what makes a warning feel like intelligence rather than a
 * notification: "seen on the pass to Deepgate" tells the player how long they
 * have without quoting them a number twice.
 */
export function roadName(c: CampaignState, from: number, to: number): string {
  const link = linkBetween(c, from, to);
  const kind = LINK_NAMES[link ? link.kind : LinkKind.Wild] ?? 'road';
  return `the ${kind} between ${provinceName(from)} and ${provinceName(to)}`;
}

/** Announce a meeting on the road. Kept here so both callers phrase it alike. */
export function logRoadContact(c: CampaignState, contact: RoadContact): void {
  logLine(
    c,
    `${contact.attacker.name} catches ${contact.defender.name} on ` +
      `${roadName(c, contact.from, contact.to)}.`,
  );
}
